
import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Users,
  UserPlus,
  Search as SearchIcon,
  Download,
  Mail,
  Phone,
  Calendar,
  TrendingUp,
  Star,
  MoreVertical,
  Eye,
  Send,
  Gift,
  Clock,
  DollarSign,
  ArrowUpRight,
  UserCheck,
  UserX,
  Crown,
  Activity,
  ChevronRight,
  Package,
  Utensils,
  MessageSquare,
  History,
  Ban,
  CheckCircle,
  Edit,
  FileText,
  CreditCard,
  Loader2,
  Megaphone,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";

import {
  addCustomerNote,
  createCustomer,
  getCustomerDetails,
  getCustomerOrders,
  listCustomers,
  updateCustomer,
  sendDirectCustomerOutreach,
  type CreateCustomerInput,
  type CustomerOrder,
  type ListCustomersResponse,
  type UpdateCustomerInput,
} from "@/lib/api/customers";
import { fetchPayments, type PaymentRecord } from "@/lib/api/payments";
import { useTenantSlug } from "@/stores/tenantStore";
import { useLocaleFormatter } from "@/lib/i18n/useLocaleFormatter";
import {
  centsToAmount,
  toDateFromMillis,
  transformCustomer,
  type Customer,
  type CustomerStatus,
} from "@/features/restaurant/utils/customer-directory";

const EMPTY_CUSTOMER_ORDERS: CustomerOrder[] = [];
const EMPTY_PAYMENT_RESULTS: PaymentRecord[] = [];

type CustomerActionState =
  | { type: "orders"; customerId: string }
  | { type: "payments"; customerId: string }
  | { type: "promotion"; customerId: string }
  | { type: "sms"; customerId: string }
  | { type: "loyalty"; customerId: string }
  | { type: "vip"; customerId: string; mode: "promote" | "remove" }
  | { type: "reservation"; customerId: string }
  | { type: "status"; customerId: string; mode: "suspend" | "reactivate" }
  | { type: "export"; customerId: string };

type CustomerOverride = {
  status?: CustomerStatus;
  loyaltyPoints?: number;
};

const formatDate = (date: Date | null) => {
  if (!date) {
    return "-";
  }
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const formatDateTime = (timestamp: number | null | undefined) => {
  if (typeof timestamp !== "number" || !Number.isFinite(timestamp)) {
    return "-";
  }
  return new Date(timestamp).toLocaleString("en-US", {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getDaysSinceLastVisit = (date: Date | null) => {
  if (!date) {
    return "No visits yet";
  }
  const days = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
  return `${Math.floor(days / 30)} months ago`;
};

const getInitials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

const normalizePhoneDigits = (value: string | null | undefined): string => {
  return (value ?? "").replace(/\D+/g, "");
};

const CustomersPageV3 = () => {
  const { subdomain } = useParams<{ subdomain?: string }>();
  const storeSlug = useTenantSlug();
  const storedSlug = typeof window !== "undefined" ? localStorage.getItem("tenantSlug") ?? undefined : undefined;
  const tenantSlug = subdomain ?? storeSlug ?? storedSlug ?? (import.meta.env.DEV ? "demo" : undefined);
  const hasTenantSlug = Boolean(tenantSlug);

  const { formatCurrency: localeFormatCurrency } = useLocaleFormatter();
  const formatCurrency = useCallback(
    (amount: number, currency?: string | null) =>
      localeFormatCurrency(amount ?? 0, (currency ?? "USD").toUpperCase()),
    [localeFormatCurrency],
  );

  const queryClient = useQueryClient();
  const [newCustomerName, setNewCustomerName] = useState("");
  const [newCustomerEmail, setNewCustomerEmail] = useState("");
  const [newCustomerPhone, setNewCustomerPhone] = useState("");
  const [newCustomerNotes, setNewCustomerNotes] = useState("");
  const [createFormError, setCreateFormError] = useState<string | null>(null);
  const [noteInput, setNoteInput] = useState("");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFormState, setEditFormState] = useState({ name: "", email: "", phone: "", notes: "" });

  const resetCreateForm = () => {
    setNewCustomerName("");
    setNewCustomerEmail("");
    setNewCustomerPhone("");
    setNewCustomerNotes("");
    setCreateFormError(null);
  };

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loyaltyFilter, setLoyaltyFilter] = useState<string | null>(null);

  const customersQuery = useQuery({
    queryKey: ["customers", "list", tenantSlug ?? "__no-tenant__", searchQuery, statusFilter, loyaltyFilter],
    queryFn: ({ signal }) => {
      if (!tenantSlug) {
        return Promise.resolve({ customers: [] } satisfies ListCustomersResponse);
      }
      return listCustomers({
        tenantSlug,
        signal,
        search: searchQuery.length >= 2 ? searchQuery : undefined,
        loyaltyState: loyaltyFilter === "vip" ? "vip" : loyaltyFilter === "active" ? "active" : loyaltyFilter === "lapsed" ? "lapsed" : loyaltyFilter === "suspended" ? "suspended" : undefined,
      });
    },
    select: (payload) => payload.customers ?? [],
    enabled: hasTenantSlug,
    staleTime: 60_000,
  });

  const [customerOverrides, setCustomerOverrides] = useState<Record<string, CustomerOverride>>({});
  const [actionState, setActionState] = useState<CustomerActionState | null>(null);
  const [promotionForm, setPromotionForm] = useState({ subject: "", message: "" });
  const [smsMessage, setSmsMessage] = useState("");
  const [loyaltyPointsInput, setLoyaltyPointsInput] = useState(50);
  const [reservationForm, setReservationForm] = useState({ date: "", time: "", partySize: 2, note: "" });
  const [isExporting, setIsExporting] = useState(false);
  const [actionSubmitting, setActionSubmitting] = useState(false);
  const [detailFocus, setDetailFocus] = useState<"notes" | null>(null);
  const [highlightSection, setHighlightSection] = useState<"notes" | null>(null);
  const notesSectionRef = useRef<HTMLDivElement | null>(null);

  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);

  const customers = useMemo(() => {
    const base = (customersQuery.data ?? []).map(transformCustomer);
    if (Object.keys(customerOverrides).length === 0) {
      return base;
    }
    return base.map((customer) => {
      const override = customerOverrides[customer.id];
      if (!override) {
        return customer;
      }
      return {
        ...customer,
        ...(override.status ? { status: override.status } : {}),
        ...(override.loyaltyPoints !== undefined ? { loyaltyPoints: override.loyaltyPoints } : {}),
      };
    });
  }, [customersQuery.data, customerOverrides]);

  useEffect(() => {
    if (!actionState) {
      setPromotionForm({ subject: "", message: "" });
      setSmsMessage("");
      setLoyaltyPointsInput(50);
      setReservationForm({ date: "", time: "", partySize: 2, note: "" });
      setActionSubmitting(false);
    }
  }, [actionState]);

  useEffect(() => {
    if (!showDetailsDialog || !detailFocus) {
      return;
    }
    if (detailFocus === "notes" && notesSectionRef.current) {
      notesSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      setHighlightSection("notes");
    }
    setDetailFocus(null);
  }, [showDetailsDialog, detailFocus]);

  useEffect(() => {
    if (!highlightSection) {
      return;
    }
    const timer = window.setTimeout(() => setHighlightSection(null), 1600);
    return () => window.clearTimeout(timer);
  }, [highlightSection]);

  const closeAction = useCallback(() => {
    setActionState(null);
  }, []);

  const applyCustomerOverride = useCallback((customerId: string, override: CustomerOverride | null) => {
    setCustomerOverrides((prev) => {
      if (!override || (override.status === undefined && override.loyaltyPoints === undefined)) {
        if (!(customerId in prev)) {
          return prev;
        }
        const next = { ...prev };
        delete next[customerId];
        return next;
      }
      return {
        ...prev,
        [customerId]: {
          ...(prev[customerId] ?? {}),
          ...override,
        },
      };
    });
  }, []);

  const actionCustomer = useMemo(() => {
    if (!actionState) {
      return null;
    }
    return customers.find((customer) => customer.id === actionState.customerId) ?? null;
  }, [actionState, customers]);

  const appendCustomerNote = useCallback(
    async (customerId: string, note: string) => {
      if (!tenantSlug) {
        throw new Error("Missing tenant context");
      }
      await addCustomerNote(tenantSlug, customerId, note);
      await queryClient.invalidateQueries({ queryKey: ["customers", "detail", tenantSlug, customerId] });
      await queryClient.invalidateQueries({ queryKey: ["customers", "list", tenantSlug] });
    },
    [queryClient, tenantSlug],
  );

  const handleDetailsDialogChange = (open: boolean) => {
    setShowDetailsDialog(open);
    if (!open) {
      setSelectedCustomerId(null);
      setDetailFocus(null);
      setHighlightSection(null);
    }
  };

  const handleAddDialogChange = (open: boolean) => {
    setShowAddDialog(open);
    if (!open) {
      resetCreateForm();
    }
  };

  const handleEditDialogChange = (open: boolean) => {
    setEditDialogOpen(open);
    if (!open) {
      setEditFormState({ name: "", email: "", phone: "", notes: "" });
    }
  };

  const handleCreateCustomerSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setCreateFormError(null);

    if (!newCustomerName.trim()) {
      setCreateFormError("Customer name is required.");
      return;
    }

    const payload: CreateCustomerInput = {
      name: newCustomerName.trim(),
    };

    if (newCustomerEmail.trim()) {
      payload.email = newCustomerEmail.trim();
    }
    if (newCustomerPhone.trim()) {
      payload.phone = newCustomerPhone.trim();
    }
    if (newCustomerNotes.trim()) {
      payload.notes = newCustomerNotes.trim();
    }

    createCustomerMutation.mutate(payload);
  };

  const handleAddNoteSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedCustomerId) {
      toast.error("Select a customer before adding notes.");
      return;
    }
    if (!noteInput.trim()) {
      toast.error("Note cannot be empty.");
      return;
    }
    addNoteMutation.mutate({ customerId: selectedCustomerId, note: noteInput.trim() });
  };

  const openEditDialog = () => {
    if (!selectedCustomerId) {
      toast.error("Select a customer to edit.");
      return;
    }
    if (!isManualCustomer) {
      toast.error("Only manually created customers can be edited.");
      return;
    }
    if (!detailCustomer && !selectedCustomer) {
      toast.error("Customer details are still loading.");
      return;
    }
    setEditFormState({
      name: detailCustomer?.name ?? selectedCustomer?.name ?? "",
      email: detailCustomer?.email ?? selectedCustomer?.email ?? "",
      phone: detailCustomer?.phone ?? selectedCustomer?.phone ?? "",
      notes: detailNotes ?? "",
    });
    setEditDialogOpen(true);
  };

  const handleEditCustomerSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedCustomerId) {
      toast.error("Select a customer to edit.");
      return;
    }

    const payload: UpdateCustomerInput = {};
    if (editFormState.name.trim() !== (detailCustomer?.name ?? selectedCustomer?.name ?? "")) {
      payload.name = editFormState.name.trim();
    }
    if (editFormState.email.trim() !== (detailCustomer?.email ?? selectedCustomer?.email ?? "")) {
      payload.email = editFormState.email.trim() || null;
    }
    if (editFormState.phone.trim() !== (detailCustomer?.phone ?? selectedCustomer?.phone ?? "")) {
      payload.phone = editFormState.phone.trim() || null;
    }
    if (editFormState.notes.trim() !== (detailNotes ?? "")) {
      payload.notes = editFormState.notes.trim() || null;
    }

    if (Object.keys(payload).length === 0) {
      toast.info("No changes detected.");
      return;
    }

    updateCustomerMutation.mutate({ customerId: selectedCustomerId, data: payload });
  };

  const selectedCustomer = useMemo(
    () => (selectedCustomerId ? customers.find((customer) => customer.id === selectedCustomerId) ?? null : null),
    [customers, selectedCustomerId],
  );

  const customerDetailsQuery = useQuery({
    queryKey: ["customers", "detail", tenantSlug ?? "__no-tenant__", selectedCustomerId],
    queryFn: ({ signal }) => {
      if (!tenantSlug || !selectedCustomerId) {
        throw new Error("Customer not selected");
      }
      return getCustomerDetails({ tenantSlug, customerId: selectedCustomerId, signal });
    },
    select: (payload) => payload.customer,
    enabled: hasTenantSlug && Boolean(selectedCustomerId) && (showDetailsDialog || Boolean(actionState)),
    staleTime: 30_000,
  });

  const isLoadingCustomers = customersQuery.isLoading && customers.length === 0;
  const customersError =
    customersQuery.isError && customersQuery.error instanceof Error ? customersQuery.error.message : undefined;
  const detailLoading = customerDetailsQuery.isLoading;
  const detailError =
    customerDetailsQuery.isError && customerDetailsQuery.error instanceof Error
      ? customerDetailsQuery.error.message
      : undefined;
  const detailCustomer = customerDetailsQuery.data ?? null;

  const totalCustomers = customers.length;
  const activeCustomers = customers.filter((customer) => customer.status === "active" || customer.status === "vip").length;
  const vipCustomers = customers.filter((customer) => customer.status === "vip").length;
  const inactiveCustomers = customers.filter((customer) => customer.status === "inactive").length;
  const totalRevenue = customers.reduce((sum, customer) => sum + customer.totalSpent, 0);
  const averageOrderValue =
    customers.length === 0 ? 0 : customers.reduce((sum, customer) => sum + customer.averageOrderValue, 0) / customers.length;
  const currencyCode = customers[0]?.currency ?? "USD";
  const detailCurrency = selectedCustomer?.currency ?? currencyCode;
  const detailTotalOrders = detailCustomer?.totalOrders ?? selectedCustomer?.totalOrders ?? 0;
  const detailTotalSpent =
    typeof detailCustomer?.totalSpentCents === "number"
      ? centsToAmount(detailCustomer.totalSpentCents)
      : selectedCustomer?.totalSpent ?? 0;
  const detailAverageOrderValue =
    typeof detailCustomer?.avgOrderValueCents === "number"
      ? centsToAmount(detailCustomer.avgOrderValueCents)
      : selectedCustomer?.averageOrderValue ?? 0;
  const detailJoinDate =
    typeof detailCustomer?.firstOrderAt === "number"
      ? toDateFromMillis(detailCustomer.firstOrderAt)
      : selectedCustomer?.joinDate ?? null;
  const detailLastVisitDate =
    typeof detailCustomer?.lastOrderAt === "number"
      ? toDateFromMillis(detailCustomer.lastOrderAt)
      : selectedCustomer?.lastVisit ?? null;
  const detailNotes = detailCustomer?.legacyNotes ?? null;
  const detailContactMethods = detailCustomer?.contactMethods ?? [];
  const detailPreferences = detailCustomer?.preferences ?? [];
  const detailStructuredNotes = detailCustomer?.notes ?? [];
  const detailRecentOrders = detailCustomer?.recentOrders ?? [];
  const isManualCustomer = detailCustomer?.kind === "manual" || (selectedCustomerId?.startsWith("profile:") ?? false);
  const displayName = detailCustomer?.name ?? selectedCustomer?.name ?? "Guest";
  const displayEmail = detailCustomer?.email ?? selectedCustomer?.email ?? null;
  const displayPhone = detailCustomer?.phone ?? selectedCustomer?.phone ?? null;
  const marketingOptInCustomers = customers.filter((customer) => customer.marketingOptIn).length;
  const avgLifetimeValue = totalCustomers === 0 ? 0 : totalRevenue / totalCustomers;
  const visitIntervalsMs = customers
    .map((customer) => customer.avgVisitIntervalMs)
    .filter((value): value is number => typeof value === "number" && Number.isFinite(value) && value > 0);
  const avgVisitIntervalDays = visitIntervalsMs.length > 0 ? visitIntervalsMs.reduce((sum, value) => sum + value, 0) / visitIntervalsMs.length / (1000 * 60 * 60 * 24) : null;
  const detailLoyaltyTier = detailCustomer?.loyaltyTier ?? selectedCustomer?.loyaltyTier ?? null;
  const detailLoyaltyState = detailCustomer?.loyaltyState ?? selectedCustomer?.loyaltyState ?? null;
  const detailMarketingOptIn = detailCustomer?.marketingOptIn ?? selectedCustomer?.marketingOptIn ?? false;
  const detailMarketingOptInAt = detailCustomer?.marketingOptInAt ?? selectedCustomer?.marketingOptInAt ?? null;
  const detailMarketingOptOutAt = detailCustomer?.marketingOptOutAt ?? selectedCustomer?.marketingOptOutAt ?? null;
  const detailVisitCount = detailCustomer?.visitCount ?? selectedCustomer?.visitCount ?? 0;
  const detailVisitStreak = detailCustomer?.visitStreak ?? selectedCustomer?.visitStreak ?? 0;
  const detailAvgVisitIntervalDays = (() => {
    const value = detailCustomer?.avgVisitIntervalMs ?? selectedCustomer?.avgVisitIntervalMs ?? null;
    return typeof value === "number" && Number.isFinite(value) && value > 0 ? value / (1000 * 60 * 60 * 24) : null;
  })();
  const verifiedPhoneContact = detailContactMethods.find((method) => method.type === "phone" && method.status === "verified");
  const verifiedEmailContact = detailContactMethods.find((method) => method.type === "email" && method.status === "verified");

  const shouldLoadOrders = actionState?.type === "orders" || actionState?.type === "payments";
  const ordersHistoryQuery = useQuery({
    queryKey: ["customers", "orders", tenantSlug ?? "__no-tenant__", actionState?.customerId],
    queryFn: ({ signal }) => {
      if (!tenantSlug || !actionState || !shouldLoadOrders) {
        throw new Error("Customer not selected");
      }
      return getCustomerOrders({ tenantSlug, customerId: actionState.customerId, signal });
    },
    enabled: hasTenantSlug && shouldLoadOrders && Boolean(actionState?.customerId),
    staleTime: 30_000,
  });
  const ordersHistoryData = ordersHistoryQuery.data?.orders;
  const ordersHistory = useMemo(
    () => ordersHistoryData ?? EMPTY_CUSTOMER_ORDERS,
    [ordersHistoryData],
  );

  const showPaymentsSheet = actionState?.type === "payments";
  const paymentsHistoryQuery = useQuery({
    queryKey: ["customers", "payments", tenantSlug ?? "__no-tenant__", actionState?.customerId],
    queryFn: async ({ signal }) => {
      if (!tenantSlug || !actionState || actionState.type !== "payments") {
        throw new Error("Customer not selected");
      }
      const response = await fetchPayments({ limit: 200 }, signal);
      return response.payments ?? [];
    },
    enabled: hasTenantSlug && showPaymentsSheet && Boolean(actionState?.customerId),
    staleTime: 30_000,
  });
  const paymentsHistoryData = paymentsHistoryQuery.data;
  const paymentsHistory = useMemo(
    () => paymentsHistoryData ?? EMPTY_PAYMENT_RESULTS,
    [paymentsHistoryData],
  );

  const paymentsForCustomer = useMemo(() => {
    if (!actionCustomer) {
      return EMPTY_PAYMENT_RESULTS;
    }

    const normalizedName = (actionCustomer.name ?? "").trim().toLowerCase();
    const normalizedPhone = normalizePhoneDigits(actionCustomer.phone);
    const orderIds = new Set(ordersHistory.map((order) => order.id));

    return paymentsHistory.filter((payment) => {
      if (payment.orderId && orderIds.size > 0 && orderIds.has(payment.orderId)) {
        return true;
      }

      const metadata = payment.metadata;
      if (metadata && typeof metadata === "object") {
        const record = metadata as Record<string, unknown>;
        const readString = (key: string): string | null =>
          typeof record[key] === "string" ? (record[key] as string) : null;
        const candidateIds = [readString("customerId"), readString("customer_id"), readString("customerProfileId"), readString("customer_profile_id")].filter(
          Boolean,
        ) as string[];

        if (candidateIds.some((value) => value === actionCustomer.id)) {
          return true;
        }

        const metadataPhone = readString("customerPhone") ?? readString("customer_phone");

        if (metadataPhone && normalizedPhone) {
          if (normalizePhoneDigits(metadataPhone) === normalizedPhone) {
            return true;
          }
        }
      }

      const paymentName = (payment.customerName ?? "").trim().toLowerCase();
      if (normalizedName && paymentName) {
        return paymentName === normalizedName;
      }
      if (!normalizedName && !paymentName) {
        return true;
      }
      return false;
    });
  }, [actionCustomer, ordersHistory, paymentsHistory]);

  const isPaymentsLoading =
    actionState?.type === "payments" && (paymentsHistoryQuery.isLoading || ordersHistoryQuery.isLoading);
  const paymentsQueryError =
    actionState?.type === "payments" && (paymentsHistoryQuery.isError || ordersHistoryQuery.isError);

  const createCustomerMutation = useMutation({
    mutationFn: (payload: CreateCustomerInput) => {
      if (!tenantSlug) {
        throw new Error("Missing tenant context");
      }
      return createCustomer(tenantSlug, payload);
    },
    onSuccess: async ({ customer }) => {
      await queryClient.invalidateQueries({ queryKey: ["customers", "list", tenantSlug] });
      toast.success(`Customer ${customer.name ?? "created"} added successfully.`);
      handleAddDialogChange(false);
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : "Failed to create customer.";
      toast.error(message);
    },
  });

  const updateCustomerMutation = useMutation({
    mutationFn: ({ customerId, data }: { customerId: string; data: UpdateCustomerInput }) => {
      if (!tenantSlug) {
        throw new Error("Missing tenant context");
      }
      return updateCustomer(tenantSlug, customerId, data);
    },
    onSuccess: async (_payload, variables) => {
      await queryClient.invalidateQueries({ queryKey: ["customers", "list", tenantSlug] });
      await queryClient.invalidateQueries({ queryKey: ["customers", "detail", tenantSlug, variables.customerId] });
      toast.success("Customer details updated.");
      setEditDialogOpen(false);
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : "Failed to update customer.";
      toast.error(message);
    },
  });

  const addNoteMutation = useMutation({
    mutationFn: ({ customerId, note }: { customerId: string; note: string }) => {
      if (!tenantSlug) {
        throw new Error("Missing tenant context");
      }
      return addCustomerNote(tenantSlug, customerId, note);
    },
    onSuccess: async (_response, variables) => {
      await queryClient.invalidateQueries({ queryKey: ["customers", "list", tenantSlug] });
      await queryClient.invalidateQueries({ queryKey: ["customers", "detail", tenantSlug, variables.customerId] });
      setNoteInput("");
      toast.success("Note added to customer profile.");
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : "Failed to add note.";
      toast.error(message);
    },
  });

  const handlePromotionSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!actionState || actionState.type !== "promotion" || !actionCustomer) {
      return;
    }
    if (!promotionForm.subject.trim() || !promotionForm.message.trim()) {
      toast.error("Both subject and message are required.");
      return;
    }
    if (!verifiedEmailContact) {
      toast.error("No deliverable email contact for this customer.");
      return;
    }
    if (!tenantSlug) {
      toast.error("Tenant context unavailable.");
      return;
    }
    try {
      setActionSubmitting(true);
      const profileId = parseProfileId(actionCustomer.id);
      const response = await sendDirectCustomerOutreach({
        tenantSlug,
        profileIds: [profileId],
        channel: "email",
        message: promotionForm.message.trim(),
        subject: promotionForm.subject.trim(),
      });
      if (response.failed > 0) {
        const failureReason = response.failures && response.failures[0] ? response.failures[0].reason : "Unable to send email.";
        toast.error(failureReason);
      } else {
        await appendCustomerNote(
          actionCustomer.id,
          `[Promotion] ${promotionForm.subject.trim()}\n${promotionForm.message.trim()}`,
        );
        toast.success(`Email sent to ${actionCustomer.name}.`);
      }
      closeAction();
    } catch (error) {
      console.error(error);
      toast.error("Unable to send email.");
    } finally {
      setActionSubmitting(false);
    }
  };

  const handleSmsSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!actionState || actionState.type !== "sms" || !actionCustomer) {
      return;
    }
    if (!smsMessage.trim()) {
      toast.error("Message cannot be empty.");
      return;
    }
    if (!verifiedPhoneContact) {
      toast.error("No verified phone contact for this customer.");
      return;
    }
    if (!tenantSlug) {
      toast.error("Tenant context unavailable.");
      return;
    }
    try {
      setActionSubmitting(true);
      const profileId = parseProfileId(actionCustomer.id);
      const response = await sendDirectCustomerOutreach({
        tenantSlug,
        profileIds: [profileId],
        channel: "sms",
        message: smsMessage.trim(),
      });
      if (response.failed > 0) {
        const failureReason = response.failures && response.failures[0] ? response.failures[0].reason : "Unable to send SMS.";
        toast.error(failureReason);
      } else {
        await appendCustomerNote(actionCustomer.id, `[SMS] ${smsMessage.trim()}`);
        toast.success("SMS sent successfully.");
      }
      closeAction();
    } catch (error) {
      console.error(error);
      toast.error("Unable to send SMS notification.");
    } finally {
      setActionSubmitting(false);
    }
  };

  const handleLoyaltySubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!actionState || actionState.type !== "loyalty" || !actionCustomer) {
      return;
    }
    const points = Number(loyaltyPointsInput);
    if (!Number.isFinite(points) || points <= 0) {
      toast.error("Enter a positive number of points.");
      return;
    }
    try {
      setActionSubmitting(true);
      const currentPoints =
        customerOverrides[actionCustomer.id]?.loyaltyPoints ?? actionCustomer.loyaltyPoints ?? 0;
      const newTotal = currentPoints + Math.round(points);
      applyCustomerOverride(actionCustomer.id, { loyaltyPoints: newTotal });
      await appendCustomerNote(
        actionCustomer.id,
        `[Loyalty] Added ${Math.round(points)} points (new balance: ${newTotal}).`,
      );
      toast.success(`${Math.round(points)} loyalty points added.`);
      closeAction();
    } catch (error) {
      console.error(error);
      toast.error("Unable to update loyalty points.");
    } finally {
      setActionSubmitting(false);
    }
  };

  const handleVipConfirmation = async () => {
    if (!actionState || actionState.type !== "vip" || !actionCustomer) {
      return;
    }
    const targetStatus: CustomerStatus = actionState.mode === "promote" ? "vip" : "active";
    try {
      setActionSubmitting(true);
      applyCustomerOverride(actionCustomer.id, { status: targetStatus });
      await appendCustomerNote(
        actionCustomer.id,
        actionState.mode === "promote" ? "[Status] Promoted customer to VIP." : "[Status] VIP status removed.",
      );
      toast.success(
        actionState.mode === "promote" ? "Customer promoted to VIP." : "Customer returned to regular status.",
      );
      closeAction();
    } catch (error) {
      console.error(error);
      toast.error("Unable to update VIP status.");
    } finally {
      setActionSubmitting(false);
    }
  };

  const handleAccountStatusConfirmation = async () => {
    if (!actionState || actionState.type !== "status" || !actionCustomer) {
      return;
    }
    const targetStatus: CustomerStatus = actionState.mode === "suspend" ? "inactive" : "active";
    try {
      setActionSubmitting(true);
      applyCustomerOverride(actionCustomer.id, { status: targetStatus });
      await appendCustomerNote(
        actionCustomer.id,
        actionState.mode === "suspend"
          ? "[Status] Account suspended."
          : "[Status] Account reactivated and marked active.",
      );
      toast.success(
        actionState.mode === "suspend" ? "Customer account suspended." : "Customer account reactivated.",
      );
      closeAction();
    } catch (error) {
      console.error(error);
      toast.error("Unable to update account status.");
    } finally {
      setActionSubmitting(false);
    }
  };

  const handleReservationSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!actionState || actionState.type !== "reservation" || !actionCustomer) {
      return;
    }
    if (!reservationForm.date || !reservationForm.time || !reservationForm.partySize) {
      toast.error("Complete all reservation details.");
      return;
    }
    try {
      setActionSubmitting(true);
      const note = `[Reservation] Scheduled for ${reservationForm.date} at ${reservationForm.time} for party of ${reservationForm.partySize}${
        reservationForm.note ? `. Notes: ${reservationForm.note}` : ""
      }.`;
      await appendCustomerNote(actionCustomer.id, note);
      toast.success("Reservation recorded.");
      closeAction();
    } catch (error) {
      console.error(error);
      toast.error("Unable to schedule reservation.");
    } finally {
      setActionSubmitting(false);
    }
  };

  const handleExportData = async () => {
    if (!actionState || actionState.type !== "export" || !actionCustomer || !tenantSlug) {
      return;
    }
    try {
      setIsExporting(true);
      const detail = await queryClient.fetchQuery({
        queryKey: ["customers", "detail", tenantSlug, actionCustomer.id],
        queryFn: ({ signal }) => getCustomerDetails({ tenantSlug, customerId: actionCustomer.id, signal }),
      });
      const payload = {
        customer: actionCustomer,
        detail: detail.customer,
      };
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${(actionCustomer.name || "customer").replace(/\s+/g, "_")}_export.json`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
      toast.success("Customer data exported.");
      closeAction();
    } catch (error) {
      console.error(error);
      toast.error("Unable to export customer data.");
    } finally {
      setIsExporting(false);
    }
  };

  const newCustomersThisMonth = customers.filter((customer) => {
    if (!customer.joinDate) {
      return false;
    }
    const monthAgo = new Date();
    monthAgo.setMonth(monthAgo.getMonth() - 1);
    return customer.joinDate > monthAgo;
  }).length;

  const filteredCustomers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return customers.filter((customer) => {
      const matchesSearch =
        query.length === 0 ||
        customer.name.toLowerCase().includes(query) ||
        (customer.email ?? "").toLowerCase().includes(query) ||
        (customer.phone ?? "").toLowerCase().includes(query) ||
        customer.id.toLowerCase().includes(query);

      const matchesStatus = statusFilter === "all" || customer.status === statusFilter;
      const matchesLoyalty =
        !loyaltyFilter || loyaltyFilter.length === 0 || (customer.loyaltyState ?? "").toLowerCase() === loyaltyFilter.toLowerCase();

      return matchesSearch && matchesStatus && matchesLoyalty;
    });
  }, [customers, searchQuery, statusFilter, loyaltyFilter]);

  const vipOnlyCustomers = useMemo(() => customers.filter((customer) => customer.status === "vip"), [customers]);

  if (!hasTenantSlug) {
    return (
      <div className="p-8">
        <Card className="mx-auto max-w-3xl border-dashed border-gray-300 dark:border-gray-700">
          <CardHeader>
            <CardTitle>Select a restaurant</CardTitle>
            <CardDescription>
              Choose a location to load customer data. If you believe this is an error, refresh the page or contact an administrator.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const getStatusBadge = (status: CustomerStatus) => {
    const styles: Record<CustomerStatus, string> = {
      active: "bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-600",
      inactive: "bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-600",
      vip: "bg-gray-900 dark:bg-gray-100 text-gray-100 dark:text-gray-900 border-gray-900 dark:border-gray-100",
    };

    const icons: Record<CustomerStatus, JSX.Element> = {
      active: <UserCheck className="h-3 w-3" />,
      inactive: <UserX className="h-3 w-3" />,
      vip: <Crown className="h-3 w-3" />,
    };

    return (
      <Badge variant="outline" className={`${styles[status]} inline-flex items-center gap-1.5 px-2.5 py-1 h-7`}>
        {icons[status]}
        <span className="capitalize">{status}</span>
      </Badge>
    );
  };

  const getLoyaltyBadge = (customer: Customer) => {
    const tier = (customer.loyaltyTier ?? 'none').toLowerCase();
    const state = (customer.loyaltyState ?? 'unknown').toLowerCase();
    const variantClasses: Record<string, string> = {
      vip: 'bg-amber-500/10 border-amber-500/50 text-amber-600 dark:text-amber-300',
      gold: 'bg-yellow-500/10 border-yellow-500/50 text-yellow-600 dark:text-yellow-300',
      silver: 'bg-gray-500/10 border-gray-400/50 text-gray-600 dark:text-gray-200',
      bronze: 'bg-orange-500/10 border-orange-500/50 text-orange-600 dark:text-orange-300',
      none: 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600',
    };
    const badgeClass = variantClasses[tier] ?? variantClasses.none;
    return (
      <Badge variant="outline" className={`inline-flex flex-col items-start px-2.5 py-1 border ${badgeClass}`}>
        <span className="text-xs font-semibold uppercase">{tier === 'none' ? 'No Tier' : tier}</span>
        <span className="text-[10px] leading-tight text-muted-foreground capitalize">{state} state</span>
      </Badge>
    );
  };

  const getOrderTypeBadge = (customer: Customer) => {
    if (customer.orderType === "pickup") {
      return (
        <Badge variant="outline" className="bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-600 inline-flex items-center gap-1.5 px-2.5 py-1 h-7">
          <Package className="h-3 w-3" />
          <span>Pick Up</span>
        </Badge>
      );
    }

    return (
      <Badge variant="outline" className="bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-600 inline-flex items-center gap-1.5 px-2.5 py-1 h-7">
        <Utensils className="h-3 w-3" />
        <span>{customer.currentTable ?? customer.preferredTable ?? "Dine-In"}</span>
      </Badge>
    );
  };

  return (
    <div className="p-8 space-y-8">
      <div className="flex items-start justify-between">
        <div className="space-y-1.5">
          <h1 className="text-3xl font-bold tracking-tight bg-gradient-to-br from-gray-900 via-gray-800 to-gray-700 dark:from-white dark:via-gray-100 dark:to-gray-300 bg-clip-text text-transparent">
            Customer Management
          </h1>
          <p className="text-muted-foreground">Track customer relationships and engagement metrics</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() => toast.success("Exporting customer data...")}
            className="gap-2 shadow-sm hover:shadow-md transition-all duration-200 hover:-translate-y-0.5"
          >
            <Download className="h-4 w-4" />
            Export
          </Button>
          <Button
            onClick={() => setShowAddDialog(true)}
            className="gap-2 bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 text-white dark:text-gray-900 shadow-lg hover:shadow-xl transition-all duration-200 hover:-translate-y-0.5"
          >
            <UserPlus className="h-4 w-4" />
            Add Customer
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="group relative overflow-hidden border-gray-200/60 dark:border-gray-700/60 bg-white dark:bg-gray-900 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-transparent dark:from-gray-800/50 dark:to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          <CardHeader className="pb-3 relative">
            <div className="flex items-center justify-between">
              <CardDescription className="text-muted-foreground font-medium">Total Customers</CardDescription>
              <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <Users className="h-4 w-4 text-gray-700 dark:text-gray-300" />
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-2 relative">
            <div className="text-3xl font-bold tracking-tight">{totalCustomers.toLocaleString()}</div>
            <div className="flex items-center gap-1.5">
              <div className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-gray-100 dark:bg-gray-800">
                <ArrowUpRight className="h-3 w-3 text-gray-600 dark:text-gray-400" />
                <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">+{newCustomersThisMonth}</span>
              </div>
              <span className="text-xs text-muted-foreground">this month</span>
            </div>
          </CardContent>
        </Card>

        <Card className="group relative overflow-hidden border-gray-200/60 dark:border-gray-700/60 bg-white dark:bg-gray-900 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-transparent dark:from-gray-800/50 dark:to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          <CardHeader className="pb-3 relative">
            <div className="flex items-center justify-between">
              <CardDescription className="text-muted-foreground font-medium">Active Customers</CardDescription>
              <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <Activity className="h-4 w-4 text-gray-700 dark:text-gray-300" />
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-2 relative">
            <div className="text-3xl font-bold tracking-tight">{activeCustomers}</div>
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                {totalCustomers === 0 ? "0.0" : ((activeCustomers / totalCustomers) * 100).toFixed(1)}%
              </span>
              <span className="text-xs text-muted-foreground">• {vipCustomers} VIP members</span>
            </div>
            {avgVisitIntervalDays !== null && (
              <div className="text-xs text-muted-foreground">Avg visit interval {avgVisitIntervalDays.toFixed(1)} days</div>
            )}
          </CardContent>
        </Card>

        <Card className="group relative overflow-hidden border-gray-200/60 dark:border-gray-700/60 bg-white dark:bg-gray-900 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-transparent dark:from-gray-800/50 dark:to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          <CardHeader className="pb-3 relative">
            <div className="flex items-center justify-between">
              <CardDescription className="text-muted-foreground font-medium">Customer Lifetime Value</CardDescription>
              <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <Sparkles className="h-4 w-4 text-gray-700 dark:text-gray-300" />
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-2 relative">
            <div className="text-3xl font-bold tracking-tight">{formatCurrency(avgLifetimeValue, currencyCode)}</div>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <DollarSign className="h-3 w-3" />
              Average lifetime revenue per customer
            </div>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <TrendingUp className="h-3 w-3" />
              Avg order value {formatCurrency(averageOrderValue, currencyCode)}
            </div>
          </CardContent>
        </Card>

        <Card className="group relative overflow-hidden border-gray-200/60 dark:border-gray-700/60 bg-white dark:bg-gray-900 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-transparent dark:from-gray-800/50 dark:to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          <CardHeader className="pb-3 relative">
            <div className="flex items-center justify-between">
              <CardDescription className="text-muted-foreground font-medium">Marketing Opt-Ins</CardDescription>
              <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <Megaphone className="h-4 w-4 text-gray-700 dark:text-gray-300" />
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-2 relative">
            <div className="text-3xl font-bold tracking-tight">{marketingOptInCustomers}</div>
            <div className="flex items-center gap-1.5">
              <Badge variant="outline" className="uppercase text-xs">
                {totalCustomers === 0 ? "0%" : `${((marketingOptInCustomers / totalCustomers) * 100).toFixed(1)}%`} opted in
              </Badge>
              <span className="text-xs text-muted-foreground">Audience reach</span>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="all-customers" className="space-y-6">
        <div className="flex items-center justify-center">
          <TabsList className="inline-flex bg-gray-900 dark:bg-gray-100 p-1.5 rounded-full">
            <TabsTrigger
              value="all-customers"
              className="rounded-full data-[state=active]:bg-gray-700 dark:data-[state=active]:bg-gray-300 data-[state=active]:text-white dark:data-[state=active]:text-gray-900 data-[state=active]:shadow-sm text-gray-400 dark:text-gray-600 hover:text-gray-300 dark:hover:text-gray-700 transition-all px-6"
            >
              <Users className="h-4 w-4 mr-2" />
              All Customers
            </TabsTrigger>
            <TabsTrigger
              value="vip"
              className="rounded-full data-[state=active]:bg-gray-700 dark:data-[state=active]:bg-gray-300 data-[state=active]:text-white dark:data-[state=active]:text-gray-900 data-[state=active]:shadow-sm text-gray-400 dark:text-gray-600 hover:text-gray-300 dark:hover:text-gray-700 transition-all px-6"
            >
              <Crown className="h-4 w-4 mr-2" />
              VIP Members
            </TabsTrigger>
            <TabsTrigger
              value="insights"
              className="rounded-full data-[state=active]:bg-gray-700 dark:data-[state=active]:bg-gray-300 data-[state=active]:text-white dark:data-[state=active]:text-gray-900 data-[state=active]:shadow-sm text-gray-400 dark:text-gray-600 hover:text-gray-300 dark:hover:text-gray-700 transition-all px-6"
            >
              <TrendingUp className="h-4 w-4 mr-2" />
              Insights
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="all-customers" className="space-y-6">
          <Card className="border-gray-200/60 dark:border-gray-700/60 bg-white dark:bg-gray-900 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="relative flex-1">
                  <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name, email, phone, or ID..."
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    className="pl-10 bg-gray-50/50 dark:bg-gray-800/50 border-gray-200/60 dark:border-gray-700/60"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full md:w-40 bg-gray-50/50 dark:bg-gray-800/50 border-gray-200/60 dark:border-gray-700/60">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="vip">VIP</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={loyaltyFilter ?? ""} onValueChange={(value) => setLoyaltyFilter(value === "" ? null : value)}>
                  <SelectTrigger className="w-full md:w-44 bg-gray-50/50 dark:bg-gray-800/50 border-gray-200/60 dark:border-gray-700/60">
                    <SelectValue placeholder="Loyalty State" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">All Loyalty</SelectItem>
                    <SelectItem value="vip">VIP</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="lapsed">Lapsed</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                    <SelectItem value="unknown">Unknown</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card className="border-gray-200/60 dark:border-gray-700/60 bg-white dark:bg-gray-900 shadow-sm">
            <CardHeader className="border-b border-gray-200/60 dark:border-gray-700/60">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Customer Directory</CardTitle>
                  <CardDescription className="mt-1">
                    {isLoadingCustomers
                      ? "Loading customers..."
                      : customersError
                      ? "Unable to load customers. Please try again."
                      : `${filteredCustomers.length} customers found`}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent border-gray-200/60 dark:border-gray-700/60">
                      <TableHead className="font-semibold">Customer</TableHead>
                      <TableHead className="font-semibold">Contact</TableHead>
                      <TableHead className="font-semibold">Orders</TableHead>
                      <TableHead className="font-semibold">Total Spent</TableHead>
                      <TableHead className="font-semibold">Order Type</TableHead>
                      <TableHead className="font-semibold">Loyalty</TableHead>
                      <TableHead className="font-semibold">Status</TableHead>
                      <TableHead className="font-semibold">Last Visit</TableHead>
                      <TableHead className="font-semibold pr-6">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoadingCustomers ? (
                      <TableRow>
                        <TableCell colSpan={9} className="h-24 text-center text-muted-foreground">
                          Loading customer data...
                        </TableCell>
                      </TableRow>
                    ) : customersError ? (
                      <TableRow>
                        <TableCell colSpan={9} className="h-24 text-center">
                          <div className="flex flex-col items-center gap-3">
                            <p className="text-sm text-muted-foreground">{customersError}</p>
                            <Button size="sm" variant="outline" onClick={() => customersQuery.refetch()}>
                              Retry
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : filteredCustomers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="h-24 text-center text-muted-foreground">
                          No customers match the current filters.
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredCustomers.map((customer) => (
                        <TableRow
                          key={customer.id}
                          className="group border-gray-200/60 dark:border-gray-700/60 hover:bg-gray-50/50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer"
                          onClick={() => {
                            setSelectedCustomerId(customer.id);
                            setShowDetailsDialog(true);
                          }}
                        >
                          <TableCell className="align-middle">
                            <div className="flex items-center gap-3">
                              <Avatar className="h-10 w-10 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                                <AvatarFallback className="bg-gray-900 dark:bg-gray-100 text-gray-100 dark:text-gray-900 text-sm">
                                  {getInitials(customer.name)}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-semibold">{customer.name}</p>
                                <p className="text-xs text-muted-foreground font-mono">{customer.id}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="align-middle">
                            <div className="flex flex-col gap-1">
                              <div className="flex items-center gap-2 text-sm">
                                <Mail className="h-3 w-3 text-muted-foreground" />
                                <span className="text-muted-foreground">{customer.email ?? "Email not provided"}</span>
                              </div>
                              <div className="flex items-center gap-2 text-sm">
                                <Phone className="h-3 w-3 text-muted-foreground" />
                                <span className="text-muted-foreground">{customer.phone ?? "-"}</span>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="align-middle">
                            <div className="flex flex-col">
                              <span className="font-semibold">{customer.totalOrders}</span>
                              <span className="text-xs text-muted-foreground">
                                {formatCurrency(customer.averageOrderValue, customer.currency)} avg
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="align-middle">
                            <div className="flex flex-col">
                              <span className="font-semibold">{formatCurrency(customer.totalSpent, customer.currency)}</span>
                              <span className="text-xs text-muted-foreground">Lifetime value</span>
                            </div>
                          </TableCell>
                          <TableCell className="align-middle">
                            <div className="flex items-center">{getOrderTypeBadge(customer)}</div>
                          </TableCell>
                          <TableCell className="align-middle">
                            <div className="flex flex-col gap-1">
                              {getLoyaltyBadge(customer)}
                              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                <Star className="h-3 w-3 fill-gray-400 dark:fill-gray-600" />
                                {customer.loyaltyPoints} pts
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="align-middle">
                            <div className="flex items-center">{getStatusBadge(customer.status)}</div>
                          </TableCell>
                          <TableCell className="align-middle">
                            <div className="flex flex-col">
                              <span className="text-sm">{getDaysSinceLastVisit(customer.lastVisit)}</span>
                              <span className="text-xs text-muted-foreground">{formatDate(customer.lastVisit)}</span>
                            </div>
                          </TableCell>
                          <TableCell className="align-middle pr-6">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                                  onClick={(event) => event.stopPropagation()}
                                >
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-56">
                                <DropdownMenuItem
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setSelectedCustomerId(customer.id);
                                    setShowDetailsDialog(true);
                                  }}
                                >
                                  <Eye className="h-4 w-4 mr-2" />
                                  View Full Profile
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setSelectedCustomerId(customer.id);
                                    setActionState({ type: "orders", customerId: customer.id });
                                  }}
                                >
                                  <History className="h-4 w-4 mr-2" />
                                  Order History
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setSelectedCustomerId(customer.id);
                                    setActionState({ type: "payments", customerId: customer.id });
                                  }}
                                >
                                  <CreditCard className="h-4 w-4 mr-2" />
                                  Payment History
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setSelectedCustomerId(customer.id);
                                    setActionState({ type: "promotion", customerId: customer.id });
                                  }}
                                >
                                  <Mail className="h-4 w-4 mr-2" />
                                  Send Promotion
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setSelectedCustomerId(customer.id);
                                    setActionState({ type: "sms", customerId: customer.id });
                                  }}
                                >
                                  <MessageSquare className="h-4 w-4 mr-2" />
                                  Send SMS
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setSelectedCustomerId(customer.id);
                                    setActionState({ type: "loyalty", customerId: customer.id });
                                  }}
                                >
                                  <Gift className="h-4 w-4 mr-2" />
                                  Add Loyalty Points
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                {customer.status === "vip" ? (
                                  <DropdownMenuItem
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      setSelectedCustomerId(customer.id);
                                      setActionState({ type: "vip", customerId: customer.id, mode: "remove" });
                                    }}
                                  >
                                    <Crown className="h-4 w-4 mr-2" />
                                    Remove VIP Status
                                  </DropdownMenuItem>
                                ) : (
                                  <DropdownMenuItem
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      setSelectedCustomerId(customer.id);
                                      setActionState({ type: "vip", customerId: customer.id, mode: "promote" });
                                    }}
                                  >
                                    <Crown className="h-4 w-4 mr-2" />
                                    Promote to VIP
                                  </DropdownMenuItem>
                                )}
                                {customer.orderType === "dine-in" && (
                                  <DropdownMenuItem
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setSelectedCustomerId(customer.id);
                                    setActionState({ type: "reservation", customerId: customer.id });
                                  }}
                                  >
                                    <Calendar className="h-4 w-4 mr-2" />
                                    Schedule Reservation
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setSelectedCustomerId(customer.id);
                                    setShowDetailsDialog(true);
                                    setDetailFocus("notes");
                                  }}
                                >
                                  <Edit className="h-4 w-4 mr-2" />
                                  Edit Notes
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setSelectedCustomerId(customer.id);
                                    setActionState({ type: "export", customerId: customer.id });
                                  }}
                                >
                                  <FileText className="h-4 w-4 mr-2" />
                                  Export Data
                                </DropdownMenuItem>
                                {customer.status === "inactive" ? (
                                  <DropdownMenuItem
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      setSelectedCustomerId(customer.id);
                                      setActionState({ type: "status", customerId: customer.id, mode: "reactivate" });
                                    }}
                                  >
                                    <CheckCircle className="h-4 w-4 mr-2 text-emerald-600" />
                                    Reactivate Account
                                  </DropdownMenuItem>
                                ) : (
                                  <DropdownMenuItem
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      setSelectedCustomerId(customer.id);
                                      setActionState({ type: "status", customerId: customer.id, mode: "suspend" });
                                    }}
                                    className="text-red-600 dark:text-red-400 focus:text-red-600 dark:focus:text-red-400"
                                  >
                                    <Ban className="h-4 w-4 mr-2" />
                                    Suspend Account
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="vip" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {vipOnlyCustomers.length === 0 ? (
              <Card className="border-gray-200/60 dark:border-gray-700/60 bg-white dark:bg-gray-900 shadow-sm col-span-full">
                <CardContent className="py-12 text-center text-muted-foreground">
                  No VIP members yet. Promote engaged customers to unlock rewards.
                </CardContent>
              </Card>
            ) : (
              vipOnlyCustomers.map((customer) => (
                <Card
                  key={customer.id}
                  className="group border-gray-200/60 dark:border-gray-700/60 bg-white dark:bg-gray-900 shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer"
                  onClick={() => {
                    setSelectedCustomerId(customer.id);
                    setShowDetailsDialog(true);
                  }}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <Avatar className="h-16 w-16 bg-gray-900 dark:bg-gray-100 border-2 border-gray-900 dark:border-gray-100">
                        <AvatarFallback className="bg-gray-900 dark:bg-gray-100 text-gray-100 dark:text-gray-900 text-lg">
                          {getInitials(customer.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-lg">{customer.name}</h3>
                              {getOrderTypeBadge(customer)}
                            </div>
                            <p className="text-sm text-muted-foreground">{customer.email ?? "Email not provided"}</p>
                          </div>
                          <Badge className="bg-gray-900 dark:bg-gray-100 text-gray-100 dark:text-gray-900 border-gray-900 dark:border-gray-100">
                            <Crown className="h-3 w-3 mr-1" />
                            VIP
                          </Badge>
                        </div>

                        <div className="grid grid-cols-3 gap-4 mb-4">
                          <div className="p-3 rounded-lg bg-gray-50/50 dark:bg-gray-800/50">
                            <p className="text-xs text-muted-foreground mb-1">Total Spent</p>
                            <p className="font-semibold">{formatCurrency(customer.totalSpent, customer.currency)}</p>
                          </div>
                          <div className="p-3 rounded-lg bg-gray-50/50 dark:bg-gray-800/50">
                            <p className="text-xs text-muted-foreground mb-1">Orders</p>
                            <p className="font-semibold">{customer.totalOrders}</p>
                          </div>
                          <div className="p-3 rounded-lg bg-gray-50/50 dark:bg-gray-800/50">
                            <p className="text-xs text-muted-foreground mb-1">Points</p>
                            <p className="font-semibold">{customer.loyaltyPoints}</p>
                          </div>
                        </div>

                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Clock className="h-4 w-4" />
                            <span>{getDaysSinceLastVisit(customer.lastVisit)}</span>
                          </div>
                          <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        <TabsContent value="insights" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-gray-200/60 dark:border-gray-700/60 bg-white dark:bg-gray-900 shadow-sm">
              <CardHeader className="border-b border-gray-200/60 dark:border-gray-700/60">
                <CardTitle>Customer Segments</CardTitle>
                <CardDescription>Distribution by status and engagement</CardDescription>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                {[
                  { label: "VIP Members", count: vipCustomers, percentage: totalCustomers === 0 ? "0.0" : ((vipCustomers / totalCustomers) * 100).toFixed(1), icon: Crown },
                  {
                    label: "Active Customers",
                    count: activeCustomers - vipCustomers,
                    percentage:
                      totalCustomers === 0
                        ? "0.0"
                        : (((activeCustomers - vipCustomers) / totalCustomers) * 100).toFixed(1),
                    icon: UserCheck,
                  },
                  {
                    label: "Inactive Customers",
                    count: inactiveCustomers,
                    percentage: totalCustomers === 0 ? "0.0" : ((inactiveCustomers / totalCustomers) * 100).toFixed(1),
                    icon: UserX,
                  },
                ].map((segment) => {
                  const Icon = segment.icon;
                  return (
                    <div key={segment.label} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800">
                            <Icon className="h-4 w-4 text-gray-700 dark:text-gray-300" />
                          </div>
                          <div>
                            <p className="font-medium">{segment.label}</p>
                            <p className="text-xs text-muted-foreground">{segment.percentage}% of total</p>
                          </div>
                        </div>
                        <p className="font-semibold">{segment.count}</p>
                      </div>
                      <div className="h-2 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-gray-600 to-gray-700 dark:from-gray-400 dark:to-gray-500 transition-all duration-500"
                          style={{ width: `${segment.percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            <Card className="border-gray-200/60 dark:border-gray-700/60 bg-white dark:bg-gray-900 shadow-sm">
              <CardHeader className="border-b border-gray-200/60 dark:border-gray-700/60">
                <CardTitle>Top Customers</CardTitle>
                <CardDescription>By lifetime value</CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  {[...customers]
                    .sort((a, b) => b.totalSpent - a.totalSpent)
                    .slice(0, 5)
                    .map((customer, index) => (
                      <div key={customer.id} className="flex items-center gap-3">
                        <div className="w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">{index + 1}</span>
                        </div>
                        <Avatar className="h-8 w-8 bg-gray-100 dark:bg-gray-800">
                          <AvatarFallback className="bg-gray-900 dark:bg-gray-100 text-gray-100 dark:text-gray-900 text-xs">
                            {getInitials(customer.name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{customer.name}</p>
                          <p className="text-xs text-muted-foreground">{customer.totalOrders} orders</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold">{formatCurrency(customer.totalSpent, customer.currency)}</p>
                          {customer.status === "vip" && <Crown className="h-3 w-3 text-gray-600 dark:text-gray-400 ml-auto" />}
                        </div>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      <Sheet open={actionState?.type === "orders"} onOpenChange={(open) => { if (!open) closeAction(); }}>
        <SheetContent side="right" className="w-full max-w-md sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>Order History</SheetTitle>
            <SheetDescription>
              {actionCustomer ? `Recent orders for ${actionCustomer.name}.` : "Recent orders"}
            </SheetDescription>
          </SheetHeader>
          <div className="mt-6 space-y-4">
            {ordersHistoryQuery.isLoading ? (
              <div className="flex items-center justify-center py-12 text-muted-foreground">
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Loading orders...
              </div>
            ) : ordersHistoryQuery.isError ? (
              <div className="rounded-lg border border-red-200/60 dark:border-red-500/40 bg-red-500/5 px-4 py-3 text-sm text-red-700 dark:text-red-300">
                Unable to load order history. Please try again.
              </div>
            ) : ordersHistory.length === 0 ? (
              <p className="text-sm text-muted-foreground">No orders recorded for this customer yet.</p>
            ) : (
              <ul className="space-y-3">
                {ordersHistory.map((order) => (
                  <li
                    key={order.id}
                    className="rounded-lg border border-gray-200/60 dark:border-gray-700/60 bg-white dark:bg-gray-900 px-4 py-3 shadow-sm"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="font-semibold text-sm">Order #{order.id.slice(0, 8)}</p>
                        <p className="text-xs text-muted-foreground">{formatDateTime(order.createdAt)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold">
                          {formatCurrency(centsToAmount(order.totalCents ?? 0), actionCustomer?.currency ?? "USD")}
                        </p>
                        <Badge variant="outline" className="mt-1 uppercase">
                          {order.status.toLowerCase()}
                        </Badge>
                      </div>
                    </div>
                    {typeof order.tableNumber === "number" && (
                      <p className="mt-2 text-xs text-muted-foreground">Table {order.tableNumber}</p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <Sheet open={actionState?.type === "payments"} onOpenChange={(open) => { if (!open) closeAction(); }}>
        <SheetContent side="right" className="w-full max-w-md sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>Payment History</SheetTitle>
            <SheetDescription>
              {actionCustomer ? `Payments associated with ${actionCustomer.name}.` : "Payments"}
            </SheetDescription>
          </SheetHeader>
          <div className="mt-6 space-y-4">
            {isPaymentsLoading ? (
              <div className="flex items-center justify-center py-12 text-muted-foreground">
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Loading payments...
              </div>
            ) : paymentsQueryError ? (
              <div className="rounded-lg border border-red-200/60 dark:border-red-500/40 bg-red-500/5 px-4 py-3 text-sm text-red-700 dark:text-red-300">
                Unable to load payment history right now.
              </div>
            ) : paymentsForCustomer.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No payment records matched this customer in the selected timeframe.
              </p>
            ) : (
              <ul className="space-y-3">
                {paymentsForCustomer.map((payment) => (
                  <li
                    key={payment.id}
                    className="rounded-lg border border-gray-200/60 dark:border-gray-700/60 bg-white dark:bg-gray-900 px-4 py-3 shadow-sm"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="font-semibold text-sm">{payment.method.toUpperCase()}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatDateTime(Date.parse(payment.createdAt))}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold">
                          {formatCurrency(
                            centsToAmount(payment.amountCents ?? 0),
                            actionCustomer?.currency ?? payment.currency ?? "USD",
                          )}
                        </p>
                        <Badge variant="outline" className="mt-1 uppercase">
                          {payment.status}
                        </Badge>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <Dialog open={actionState?.type === "promotion"} onOpenChange={(open) => { if (!open) closeAction(); }}>
        <DialogContent className="max-w-lg">
          <form onSubmit={handlePromotionSubmit} className="space-y-4">
            <DialogHeader>
              <DialogTitle>Send Promotion</DialogTitle>
              <DialogDescription>
                {actionCustomer ? `Craft a quick promotion for ${actionCustomer.name}.` : "Send a customer promotion."}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="promotion-subject">Subject</Label>
                <Input
                  id="promotion-subject"
                  value={promotionForm.subject}
                  onChange={(event) => setPromotionForm((prev) => ({ ...prev, subject: event.target.value }))}
                  placeholder="Exclusive offer just for you"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="promotion-body">Message</Label>
                <Textarea
                  id="promotion-body"
                  value={promotionForm.message}
                  onChange={(event) => setPromotionForm((prev) => ({ ...prev, message: event.target.value }))}
                  rows={4}
                  placeholder="Share upcoming events, discounts, or loyalty perks..."
                  required
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeAction}>
                Cancel
              </Button>
              <Button type="submit" disabled={actionSubmitting || !actionCustomer}>
                {actionSubmitting ? "Sending..." : "Send Promotion"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={actionState?.type === "sms"} onOpenChange={(open) => { if (!open) closeAction(); }}>
        <DialogContent className="max-w-md">
          <form onSubmit={handleSmsSubmit} className="space-y-4">
            <DialogHeader>
              <DialogTitle>Send SMS Notification</DialogTitle>
              <DialogDescription>
                {actionCustomer
                  ? `Send a quick text update to ${actionCustomer.name}.`
                  : "Send a quick text notification."}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label htmlFor="sms-message">Message</Label>
              <Textarea
                id="sms-message"
                value={smsMessage}
                onChange={(event) => setSmsMessage(event.target.value)}
                rows={3}
                placeholder="Hello! Just a reminder about..."
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeAction}>
                Cancel
              </Button>
              <Button type="submit" disabled={actionSubmitting || !actionCustomer}>
                {actionSubmitting ? "Sending..." : "Send SMS"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={actionState?.type === "loyalty"} onOpenChange={(open) => { if (!open) closeAction(); }}>
        <DialogContent className="max-w-md">
          <form onSubmit={handleLoyaltySubmit} className="space-y-4">
            <DialogHeader>
              <DialogTitle>Add Loyalty Points</DialogTitle>
              <DialogDescription>
                {actionCustomer
                  ? `Reward ${actionCustomer.name} for their loyalty.`
                  : "Reward the customer with additional points."}
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-1 gap-3">
              <div className="space-y-2">
                <Label htmlFor="loyalty-points">Points to add</Label>
                <Input
                  id="loyalty-points"
                  type="number"
                  min={1}
                  value={loyaltyPointsInput}
                  onChange={(event) => setLoyaltyPointsInput(Number(event.target.value) || 0)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Current balance</Label>
                <p className="text-sm text-muted-foreground font-medium">
                  {(customerOverrides[actionCustomer?.id ?? ""]?.loyaltyPoints ?? actionCustomer?.loyaltyPoints ?? 0).toLocaleString()}{" "}
                  pts
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeAction}>
                Cancel
              </Button>
              <Button type="submit" disabled={actionSubmitting || !actionCustomer}>
                {actionSubmitting ? "Updating..." : "Add Points"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={actionState?.type === "vip"} onOpenChange={(open) => { if (!open) closeAction(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{actionState?.mode === "promote" ? "Promote to VIP" : "Remove VIP Status"}</DialogTitle>
            <DialogDescription>
              {actionCustomer
                ? `${actionState?.mode === "promote" ? "Upgrade" : "Update"} ${actionCustomer.name}'s VIP status.`
                : "Update VIP status."}
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {actionState?.mode === "promote"
              ? "VIP customers receive premium recognition and access to exclusive rewards."
              : "Removing VIP status will return this customer to the regular loyalty tier."}
          </p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeAction}>
              Cancel
            </Button>
            <Button onClick={handleVipConfirmation} disabled={actionSubmitting || !actionCustomer}>
              {actionSubmitting ? "Saving..." : actionState?.mode === "promote" ? "Promote to VIP" : "Remove VIP"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={actionState?.type === "status"} onOpenChange={(open) => { if (!open) closeAction(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {actionState?.mode === "suspend" ? "Suspend Account" : "Reactivate Account"}
            </DialogTitle>
            <DialogDescription>
              {actionCustomer
                ? `${actionState?.mode === "suspend" ? "Suspend" : "Reactivate"} ${actionCustomer.name}'s account.`
                : "Update customer account status."}
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {actionState?.mode === "suspend"
              ? "Suspended customers will appear as inactive within analytics."
              : "Reactivated customers will return to the active status list."}
          </p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeAction}>
              Cancel
            </Button>
            <Button
              onClick={handleAccountStatusConfirmation}
              disabled={actionSubmitting || !actionCustomer}
              className={actionState?.mode === "suspend" ? "bg-red-600 hover:bg-red-700" : undefined}
            >
              {actionSubmitting
                ? "Updating..."
                : actionState?.mode === "suspend"
                ? "Confirm Suspension"
                : "Confirm Reactivation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={actionState?.type === "reservation"} onOpenChange={(open) => { if (!open) closeAction(); }}>
        <DialogContent className="max-w-lg">
          <form onSubmit={handleReservationSubmit} className="space-y-4">
            <DialogHeader>
              <DialogTitle>Schedule Reservation</DialogTitle>
              <DialogDescription>
                {actionCustomer
                  ? `Create a reservation reminder for ${actionCustomer.name}.`
                  : "Schedule a reservation for this customer."}
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="reservation-date">Date</Label>
                <Input
                  id="reservation-date"
                  type="date"
                  value={reservationForm.date}
                  onChange={(event) => setReservationForm((prev) => ({ ...prev, date: event.target.value }))}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="reservation-time">Time</Label>
                <Input
                  id="reservation-time"
                  type="time"
                  value={reservationForm.time}
                  onChange={(event) => setReservationForm((prev) => ({ ...prev, time: event.target.value }))}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="reservation-party">Party Size</Label>
                <Input
                  id="reservation-party"
                  type="number"
                  min={1}
                  value={reservationForm.partySize}
                  onChange={(event) => setReservationForm((prev) => ({ ...prev, partySize: Number(event.target.value) || 1 }))}
                  required
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="reservation-note">Additional Notes</Label>
                <Textarea
                  id="reservation-note"
                  value={reservationForm.note}
                  onChange={(event) => setReservationForm((prev) => ({ ...prev, note: event.target.value }))}
                  rows={3}
                  placeholder="Allergies, preferred seating, celebration details..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeAction}>
                Cancel
              </Button>
              <Button type="submit" disabled={actionSubmitting || !actionCustomer}>
                {actionSubmitting ? "Scheduling..." : "Schedule Reservation"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={actionState?.type === "export"} onOpenChange={(open) => { if (!open) closeAction(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Export Customer Data</DialogTitle>
            <DialogDescription>
              {actionCustomer
                ? `Download a JSON export for ${actionCustomer.name}.`
                : "Download a JSON export for this customer."}
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            The export includes customer profile details and the latest order metrics. You can import this JSON into
            other tools for analysis or backups.
          </p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeAction}>
              Cancel
            </Button>
            <Button onClick={handleExportData} disabled={isExporting || !actionCustomer}>
              {isExporting ? "Preparing..." : "Download Export"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDetailsDialog} onOpenChange={handleDetailsDialogChange}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Customer Profile
            </DialogTitle>
            <DialogDescription>
              {selectedCustomer ? `Complete information for ${selectedCustomer.name}` : "Customer details"}
            </DialogDescription>
          </DialogHeader>

          {selectedCustomer && (
            <div className="space-y-6 py-4">
              <div className="flex items-start gap-4 p-4 rounded-lg bg-gray-50/50 dark:bg-gray-800/50">
                <Avatar className="h-16 w-16 bg-gray-900 dark:bg-gray-100 border-2 border-gray-900 dark:border-gray-100">
                  <AvatarFallback className="bg-gray-900 dark:bg-gray-100 text-gray-100 dark:text-gray-900 text-lg">
                    {getInitials(displayName)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="text-xl font-semibold">{displayName}</h3>
                      <p className="text-sm text-muted-foreground font-mono">{selectedCustomer.id}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {isManualCustomer && (
                        <Button variant="outline" size="sm" onClick={openEditDialog} disabled={updateCustomerMutation.isPending}>
                          <Edit className="h-4 w-4 mr-2" />
                          Edit Profile
                        </Button>
                      )}
                      {getStatusBadge(selectedCustomer.status)}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Mail className="h-4 w-4" />
                      {displayEmail ?? "Email not provided"}
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Phone className="h-4 w-4" />
                      {displayPhone ?? "-"}
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-4 gap-4">
                <div className="p-4 rounded-lg border border-gray-200/60 dark:border-gray-700/60 bg-gray-50/50 dark:bg-gray-800/50">
                  <p className="text-xs text-muted-foreground mb-1">Total Orders</p>
                  <p className="text-2xl font-bold">{detailTotalOrders}</p>
                </div>
                <div className="p-4 rounded-lg border border-gray-200/60 dark:border-gray-700/60 bg-gray-50/50 dark:bg-gray-800/50">
                  <p className="text-xs text-muted-foreground mb-1">Total Spent</p>
                  <p className="text-2xl font-bold">{formatCurrency(detailTotalSpent, detailCurrency)}</p>
                </div>
                <div className="p-4 rounded-lg border border-gray-200/60 dark:border-gray-700/60 bg-gray-50/50 dark:bg-gray-800/50">
                  <p className="text-xs text-muted-foreground mb-1">Avg Order</p>
                  <p className="text-2xl font-bold">{formatCurrency(detailAverageOrderValue, detailCurrency)}</p>
                </div>
                <div className="p-4 rounded-lg border border-gray-200/60 dark:border-gray-700/60 bg-gray-50/50 dark:bg-gray-800/50">
                  <p className="text-xs text-muted-foreground mb-1">Loyalty Points</p>
                  <p className="text-2xl font-bold">{selectedCustomer.loyaltyPoints}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm">Member Since</Label>
                  <p className="font-medium">{formatDate(detailJoinDate)}</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm">Last Visit</Label>
                  <p className="font-medium">{getDaysSinceLastVisit(detailLastVisitDate)}</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm">Lifetime Value</Label>
                  <p className="font-medium">{formatCurrency(detailTotalSpent, detailCurrency)}</p>
                  <p className="text-xs text-muted-foreground">{detailTotalOrders} orders • {detailVisitCount} visits</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm">Visit Cadence</Label>
                  <p className="font-medium">{detailVisitStreak} day streak</p>
                  <p className="text-xs text-muted-foreground">
                    {detailAvgVisitIntervalDays !== null ? `${detailAvgVisitIntervalDays.toFixed(1)} days avg interval` : "Not enough data"}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm">Loyalty</Label>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="uppercase text-xs">
                      {detailLoyaltyTier ? detailLoyaltyTier : "No Tier"}
                    </Badge>
                    {detailLoyaltyState && <span className="text-xs text-muted-foreground capitalize">{detailLoyaltyState} state</span>}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm">Marketing</Label>
                  <div className="flex items-center gap-2">
                    <Badge variant={detailMarketingOptIn ? "default" : "outline"} className="text-xs">
                      {detailMarketingOptIn ? "Opted In" : "Opted Out"}
                    </Badge>
                    {detailMarketingOptInAt && (
                      <span className="text-xs text-muted-foreground">since {new Date(detailMarketingOptInAt).toLocaleDateString()}</span>
                    )}
                  </div>
                  {detailMarketingOptOutAt && !detailMarketingOptIn && (
                    <p className="text-xs text-muted-foreground">Opted out {new Date(detailMarketingOptOutAt).toLocaleDateString()}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm">Order Preference</Label>
                  <div>{getOrderTypeBadge(selectedCustomer)}</div>
                </div>
                {selectedCustomer.preferredTable && (
                  <div className="space-y-2">
                    <Label className="text-muted-foreground text-sm">Preferred Table</Label>
                    <p className="font-medium">{selectedCustomer.preferredTable}</p>
                  </div>
                )}
              </div>

              {detailLoading && (
                <div className="rounded-lg border border-gray-200/60 dark:border-gray-700/60 bg-gray-50/50 dark:bg-gray-800/50 px-4 py-6 text-center text-sm text-muted-foreground">
                  Loading latest customer activity...
                </div>
              )}

              {detailError && !detailLoading && (
                <div className="rounded-lg border border-red-200/60 dark:border-red-500/40 bg-red-500/5 px-4 py-3 text-sm text-red-700 dark:text-red-300">
                  Unable to load extended customer details. Please try again.
                </div>
              )}

              {!detailLoading && !detailError && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div
                    ref={notesSectionRef}
                    className={`rounded-lg border border-gray-200/60 dark:border-gray-700/60 bg-gray-50/50 dark:bg-gray-800/50 overflow-hidden transition-shadow duration-500 ${
                      highlightSection === "notes" ? "ring-2 ring-gray-900/60 dark:ring-gray-100/60" : ""
                    }`}
                  >
                    <div className="px-4 py-3 border-b border-gray-200/60 dark:border-gray-700/60 flex items-center justify-between">
                      <p className="text-sm font-semibold">Customer Notes</p>
                      {detailStructuredNotes.length > 0 && <Badge variant="outline">{detailStructuredNotes.length}</Badge>}
                    </div>
                    <div className="px-4 py-4 text-sm leading-relaxed text-muted-foreground min-h-[96px] space-y-3">
                      {detailStructuredNotes.length > 0 ? (
                        <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
                          {detailStructuredNotes.map((entry) => (
                            <div
                              key={entry.id}
                              className="rounded-md border border-gray-200/60 dark:border-gray-700/60 bg-white/70 dark:bg-gray-900/40 px-3 py-2"
                            >
                              <div className="flex items-center justify-between text-xs text-muted-foreground">
                                <span>{entry.source ?? 'Team Note'}</span>
                                {typeof entry.createdAt === 'number' && (
                                  <span>{new Date(entry.createdAt).toLocaleString()}</span>
                                )}
                              </div>
                              <p className="mt-2 text-sm whitespace-pre-wrap text-gray-800 dark:text-gray-100">{entry.body}</p>
                            </div>
                          ))}
                        </div>
                      ) : detailNotes && detailNotes.trim().length > 0 ? (
                        <div className="rounded-md border border-gray-200/60 dark:border-gray-700/60 bg-white/70 dark:bg-gray-900/40 px-3 py-2 whitespace-pre-line">
                          {detailNotes}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">No notes recorded yet.</p>
                      )}
                    </div>
                    <form onSubmit={handleAddNoteSubmit} className="px-4 pb-4 pt-0 space-y-3">
                      <Textarea
                        placeholder="Leave a quick note about this customer..."
                        rows={3}
                        value={noteInput}
                        onChange={(event) => setNoteInput(event.target.value)}
                        disabled={addNoteMutation.isPending || !selectedCustomerId}
                        className="bg-gray-50/50 dark:bg-gray-800/50 resize-none"
                      />
                      <div className="flex justify-end">
                        <Button type="submit" size="sm" disabled={addNoteMutation.isPending || !selectedCustomerId}>
                          {addNoteMutation.isPending ? "Saving..." : "Add Note"}
                        </Button>
                      </div>
                    </form>
                  </div>

                  <div className="rounded-lg border border-gray-200/60 dark:border-gray-700/60 bg-gray-50/50 dark:bg-gray-800/50 overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-200/60 dark:border-gray-700/60 flex items-center justify-between">
                      <p className="text-sm font-semibold">Contact Methods</p>
                      <Badge variant="outline">{detailContactMethods.length}</Badge>
                    </div>
                    <div className="px-4 py-4 text-sm text-muted-foreground space-y-3">
                      {detailContactMethods.length === 0 ? (
                        <p>No contact methods recorded.</p>
                      ) : (
                        <div className="space-y-3">
                          {detailContactMethods.map((method) => (
                            <div
                              key={method.id}
                              className="flex items-start justify-between gap-3 rounded-md border border-gray-200/60 dark:border-gray-700/60 bg-white/70 dark:bg-gray-900/40 px-3 py-2"
                            >
                              <div>
                                <p className="text-sm font-semibold capitalize">{method.type.replace(/_/g, ' ')}</p>
                                <p className="text-xs text-muted-foreground">{method.value}</p>
                                {method.consentVersion && (
                                  <p className="text-xs text-muted-foreground mt-1">Consent v{method.consentVersion}</p>
                                )}
                              </div>
                              <div className="text-right">
                                <Badge variant="secondary" className="uppercase mb-1">
                                  {method.status.replace(/_/g, ' ')}
                                </Badge>
                                {typeof method.updatedAt === 'number' && (
                                  <p className="text-xs text-muted-foreground">Updated {new Date(method.updatedAt).toLocaleDateString()}</p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="rounded-lg border border-gray-200/60 dark:border-gray-700/60 bg-gray-50/50 dark:bg-gray-800/50 overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-200/60 dark:border-gray-700/60 flex items-center justify-between">
                      <p className="text-sm font-semibold">Preference Signals</p>
                      <Badge variant="outline">{detailPreferences.length}</Badge>
                    </div>
                    <div className="px-4 py-4 text-sm text-muted-foreground space-y-3">
                      {detailPreferences.length === 0 ? (
                        <p>No preferences captured yet.</p>
                      ) : (
                        <ul className="space-y-2">
                          {detailPreferences.map((preference) => {
                            const displayValue =
                              preference.value && typeof preference.value === 'object'
                                ? JSON.stringify(preference.value)
                                : String(preference.value ?? '');
                            return (
                              <li
                                key={preference.id}
                                className="rounded-md border border-gray-200/60 dark:border-gray-700/60 bg-white/70 dark:bg-gray-900/40 px-3 py-2"
                              >
                                <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                                  <span className="uppercase tracking-wide font-semibold">{preference.kind}</span>
                                  {preference.lastSeenAt && (
                                    <span>Last seen {new Date(preference.lastSeenAt).toLocaleDateString()}</span>
                                  )}
                                </div>
                                <p className="text-sm text-gray-800 dark:text-gray-100 whitespace-pre-wrap">{displayValue}</p>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  </div>

                  <div className="rounded-lg border border-gray-200/60 dark:border-gray-700/60 bg-gray-50/50 dark:bg-gray-800/50 overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-200/60 dark:border-gray-700/60 flex items-center justify-between">
                      <p className="text-sm font-semibold">Recent Orders</p>
                      <Badge variant="outline">{detailRecentOrders.length}</Badge>
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      {detailRecentOrders.length === 0 ? (
                        <div className="px-4 py-6 text-sm text-muted-foreground">No recent orders available.</div>
                      ) : (
                        <ul className="divide-y divide-gray-200/60 dark:divide-gray-700/60">
                          {detailRecentOrders.slice(0, 6).map((order) => (
                            <li key={order.id} className="px-4 py-3 flex items-center justify-between gap-4">
                              <div>
                                <p className="text-sm font-semibold">Order #{order.id.slice(0, 8)}</p>
                                <p className="text-xs text-muted-foreground">{formatDateTime(order.createdAt)}</p>
                              </div>
                              <div className="text-right">
                                <p className="text-sm font-semibold">{formatCurrency(centsToAmount(order.totalCents ?? 0), detailCurrency)}</p>
                                <Badge variant="outline" className="mt-1 uppercase">
                                  {order.status.toLowerCase()}
                                </Badge>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => handleDetailsDialogChange(false)}>
              Close
            </Button>
            <Button onClick={() => toast.success("Email sent to customer")}>
              <Send className="h-4 w-4 mr-2" />
              Send Message
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showAddDialog} onOpenChange={handleAddDialogChange}>
        <DialogContent className="max-w-2xl">
          <form onSubmit={handleCreateCustomerSubmit} className="space-y-4">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="h-5 w-5" />
                Add New Customer
              </DialogTitle>
              <DialogDescription>
                Create a new customer profile in the system
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="customer-name">Full Name *</Label>
                <Input
                  id="customer-name"
                  placeholder="John Doe"
                  value={newCustomerName}
                  onChange={(event) => setNewCustomerName(event.target.value)}
                  disabled={createCustomerMutation.isPending}
                  className="bg-gray-50/50 dark:bg-gray-800/50"
                  autoComplete="name"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="customer-email">Email Address</Label>
                <Input
                  id="customer-email"
                  type="email"
                  placeholder="john@example.com"
                  value={newCustomerEmail}
                  onChange={(event) => setNewCustomerEmail(event.target.value)}
                  disabled={createCustomerMutation.isPending}
                  className="bg-gray-50/50 dark:bg-gray-800/50"
                  autoComplete="email"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="customer-phone">Phone Number</Label>
                <Input
                  id="customer-phone"
                  type="tel"
                  placeholder="+1 (555) 123-4567"
                  value={newCustomerPhone}
                  onChange={(event) => setNewCustomerPhone(event.target.value)}
                  disabled={createCustomerMutation.isPending}
                  className="bg-gray-50/50 dark:bg-gray-800/50"
                  autoComplete="tel"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="customer-notes">Quick Notes</Label>
                <Textarea
                  id="customer-notes"
                  placeholder="Any special preferences or dietary restrictions..."
                  rows={3}
                  value={newCustomerNotes}
                  onChange={(event) => setNewCustomerNotes(event.target.value)}
                  disabled={createCustomerMutation.isPending}
                  className="bg-gray-50/50 dark:bg-gray-800/50 resize-none"
                />
              </div>
            </div>

            {createFormError && <p className="text-sm text-red-600">{createFormError}</p>}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleAddDialogChange(false)} disabled={createCustomerMutation.isPending}>
                Cancel
              </Button>
              <Button type="submit" disabled={createCustomerMutation.isPending}>
                {createCustomerMutation.isPending ? "Creating..." : "Create Customer"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={editDialogOpen} onOpenChange={handleEditDialogChange}>
        <DialogContent className="max-w-lg">
          <form onSubmit={handleEditCustomerSubmit} className="space-y-4">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit className="h-5 w-5" />
                Edit Customer Details
              </DialogTitle>
              <DialogDescription>Update contact information or internal notes for this customer.</DialogDescription>
            </DialogHeader>

            <div className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Full Name</Label>
                <Input
                  id="edit-name"
                  value={editFormState.name}
                  onChange={(event) => setEditFormState((prev) => ({ ...prev, name: event.target.value }))}
                  disabled={updateCustomerMutation.isPending}
                  autoComplete="name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={editFormState.email}
                  onChange={(event) => setEditFormState((prev) => ({ ...prev, email: event.target.value }))}
                  disabled={updateCustomerMutation.isPending}
                  autoComplete="email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-phone">Phone</Label>
                <Input
                  id="edit-phone"
                  type="tel"
                  value={editFormState.phone}
                  onChange={(event) => setEditFormState((prev) => ({ ...prev, phone: event.target.value }))}
                  disabled={updateCustomerMutation.isPending}
                  autoComplete="tel"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-notes">Notes</Label>
                <Textarea
                  id="edit-notes"
                  value={editFormState.notes}
                  onChange={(event) => setEditFormState((prev) => ({ ...prev, notes: event.target.value }))}
                  disabled={updateCustomerMutation.isPending}
                  rows={4}
                  className="resize-none"
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleEditDialogChange(false)} disabled={updateCustomerMutation.isPending}>
                Cancel
              </Button>
              <Button type="submit" disabled={updateCustomerMutation.isPending}>
                {updateCustomerMutation.isPending ? "Saving..." : "Save Changes"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomersPageV3;
